import * as React from 'react'

import "./Frame.scss";
import { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { UserDataContext } from '../contents/Contents';
import { FrameDefines } from './IFrame';
import useWindowDimensions from '../../common/useWindowDimensions';
import { OnBeforeNextStepListener } from "../contents/IContents";
import resize from './resizeManager';
import Header from '../header/Header';
import { selectColorCode } from '../../util/selectColorCode';
import Body from '../body/Body';
import Footer from '../footer/Footer';
import Progress from '../progress/Progress';
import { PersonsProps } from '../../interface/IPerson';
import { BodyProps } from '../body/IBody';
import { POSITION } from '../../common/enum/position';

type FrameProps = FrameDefines & {
  onBeforeNextStep: OnBeforeNextStepListener;
}
const Frame = (defines: FrameProps) => {
  // console.debug("Frame")
  const [userData] = useState({});
  
  const [collapse, setCollapse] = useState(defines.expanded);
  const [touched, setTouched] = useState<boolean>();
  const windowSize = useWindowDimensions();

  const isMobile = navigator.userAgent.match(/iPhone|Android.+Mobile/);
  const defPos = useMemo(() => {
    return isMobile ? defines.position.mobile : defines.position.web;
  }, [JSON.stringify(defines.position), isMobile]);

  useEffect(() => {
    // console.debug("Frame useEffect setTouched")
    if (defines.expanded != collapse) setTouched(true);
    if (touched === undefined) setTouched(false);
  }, [touched || collapse]);

  const [windowStyle, setWindowStyle] = useState<CSSProperties>({})
  useEffect(() => {
    // console.debug("Frame useEffect setWindowStyle")
    setWindowStyle(resize(
      !!isMobile,
      windowSize,
      document.getElementsByTagName("body")[0],
      defines.toggle.size.height,
      defines.main.size,
      defines.direction, defPos, collapse
    ));
  }, [
    JSON.stringify(defPos),
    defines.main.size.width,
    defines.main.size.height,
    collapse, windowSize.width, windowSize.height
  ]);

  const triggerContentDefines = useMemo(() => {
    // console.debug("Frame triggerContentDefines", JSON.stringify(defines.toggle?.content, null, 2))
    return defines.toggle?.content;
  }, [defines.toggle?.content])
  const triggerContent = useMemo(() => {
    // console.debug("Frame triggerContent", collapse)
    if (typeof triggerContentDefines === "string") return triggerContentDefines;
    return triggerContentDefines[(collapse ? "open" : "close")] || "";
  }, [collapse]);

  const [step, setStep] = useState<number>(-1);
  const [ts, setTs] = useState<{ step: number, ts: number }[]>([]);
  const startTs = useMemo(() => Date.now(), []);
  
  const setStepCount = useCallback((stepNum: number) => {
    // console.debug("Frame setStepCount")
    if (stepNum === step) return;
    setTs(ts => [ ...ts, { step: stepNum, ts: Date.now() - startTs } ]);
    setStep(stepNum);

    if (defines.main.body?.chat) {
      defines.onBeforeNextStep({step: stepNum, chatItem: defines.main.body.chat[stepNum]});
    }
  }, [step, defines, defines.main.body?.chat, defines.onBeforeNextStep]);
  
  const progress = useMemo(() => {
    return defines.main.body?.chat
      ? defines.main.body.chat.filter((c, i) => c.progress == true && i < step).length
      : 0
  }, [defines.main.body?.chat, step]);

  const personDefines = useMemo(() => {
    // console.debug("Frame personDefines", JSON.stringify(defines.main.person, null, 2))
    return defines.main.person;
  }, [])
  const preparedPersons = useMemo(() => {
    // console.debug("Frame preparedPersons")
    if (!personDefines || personDefines.length === 0) return {};
    const result = personDefines.reduce((p, c) => {
      p[c.type] = c;
      if (c.default) p.default = c;
      return p;
    }, {} as PersonsProps);
    if (!result.default) result.default = personDefines[0];
    return result;
  }, [personDefines]);
  
  const bodyProps = useMemo<BodyProps>(() => {
    // console.debug("Frame bodyProps")
    return {
      persons: preparedPersons,
      step, touched,
      ts: ts.length > 0 ? ts[ts.length - 1].ts : 0,
      setStep: setStepCount,
      collapseCallback: () => setCollapse(!collapse)
    }
  }, [
    personDefines,
    step,
    touched,
    ts,
    collapse
  ])

  const style = useMemo<CSSProperties>(() => {
    const horizontal = defines.toggle?.origin?.horizontal === POSITION.LEFT ?
      { left: 0 } : { right: 0 };
    return {
      ...horizontal,
      width: defines.toggle?.size?.width,
      height: defines.toggle?.size?.height,
      top: (defines.toggle?.size?.height != null)
        ? (typeof defines.toggle.size.height === "string"
          ? defines.toggle.size.height
          : defines.toggle.size.height * -1
        )
        : undefined,
      ...(defines.toggle?.style || {})
    }
  }, [defines.toggle?.style])

  if (touched === undefined) return <></>

  return (
    <div
      className={[
        "frame",
        defines.direction,
        defPos?.horizontal.vector,
        defPos?.vertical.vector,
        collapse ? "open" : "close"
      ].join(" ")}
      style={windowStyle}
    >
      <div
        className="trigger"
        dangerouslySetInnerHTML={{
          __html: triggerContent
        }}
        style={style}
        onClick={_ => setCollapse(!collapse)}
      />
        <div className="contents" style={{
          gridTemplateRows: `${(defines.main.header.height || 0) + 18}px 1fr ${defines.main.footer.height || 0}px`,
          backgroundColor: defines?.colors?.secondary ? defines?.colors.secondary : "#eeeeee",
          ...(defines.main.style || {})
        }}>
          {defines.main.header &&
            <Header
              height={defines.main.header.height}
              borderRadius={defines.main.header.borderRadius}
              text={defines.main.header.text}
            >
              <Progress
                bar={defines.main?.header?.progress?.bar}
                label={defines.main?.header?.progress?.label}
                progress={progress || 0}
                limit={defines.main?.body?.chat?.filter(c => c && c.progress).length || 0}
                height={18}
                doing={typeof progress === "number" ? true : false}
                colors={{
                  primary: selectColorCode([defines.colors?.progress?.primary, defines.colors?.primary]),
                  secondary: selectColorCode([defines.colors?.progress?.secondary, defines.colors?.secondary]),
                  font: selectColorCode([defines.colors?.progress?.font, defines.colors?.font])
                }}
                style={defines.main?.header?.progress?.style}
              ></Progress>
            </Header>
          }
          {defines.main.body &&
            <UserDataContext.Provider value={userData}>
              <Body
                props={bodyProps}
                defines={defines.main.body}
                colors={defines.colors}
              />
            </UserDataContext.Provider>
          }
          {defines.main.footer &&
            <Footer
              height={defines.main.footer.height}
              borderRadius={defines.main.footer.borderRadius}
            />
          }
        </div>
    </div>
  );
}

export default Frame;